/**
 * New Relic Session Helper
 *
 * Utility to help developers find their New Relic session ID for use in dashboards.
 * Call `displaySessionId()` from browser console or during development to see your session ID.
 */

/**
 * Handles PerformanceObserver entries to find New Relic beacon requests and extract session info.
 * Extracted as a top-level function for readability and testability.
 */
function handleBeaconEntries(list: PerformanceObserverEntryList, observer: PerformanceObserver): void {
    for (const entry of list.getEntries()) {
        if (entry.name.includes('bam.nr-data.net')) {
            try {
                const url = new URL(entry.name);
                const ptid = url.searchParams.get('ptid');

                if (ptid) {
                    // The ptid is NOT the session ID, we need to extract it from elsewhere
                    // Session ID is a shorter hex string like 'ee4f5d2c5cac33d5'
                    // eslint-disable-next-line no-console
                    console.info('%c🔍 New Relic Helper', 'color: #00AC69; font-weight: bold; font-size: 14px');
                    // eslint-disable-next-line no-console
                    console.info('To find your session ID, use the New Relic query below:');
                    // eslint-disable-next-line no-console
                    console.info(
                        '%cSELECT count(*) as events, latest(pageUrl) as lastPage FROM PageView, AjaxRequest WHERE appId = 1120278090 SINCE 30 minutes ago FACET session LIMIT 10',
                        'background: #f0f0f0; padding: 8px; border-left: 3px solid #00AC69'
                    );
                    // eslint-disable-next-line no-console
                    console.info('Run this query in New Relic account 2750272 to find your session ID');
                    // eslint-disable-next-line no-console
                    console.info('Your session ID will be in the "session" column (e.g., ee4f5d2c5cac33d5)');

                    // Stop observing after first beacon
                    observer.disconnect();
                    return;
                }
            } catch (error) {
                // Skip invalid URLs - continue checking other entries
                continue;
            }
        }
    }
}

/**
 * Displays the New Relic session ID in the browser console.
 *
 * The session ID is extracted from New Relic beacon requests sent to bam.nr-data.net.
 * This is the ID used in New Relic queries and dashboards.
 *
 * @example
 * // In browser console:
 * displaySessionId()
 *
 * // Or import and call:
 * import { displaySessionId } from '@dr/shared-ui-utils';
 * displaySessionId();
 */
export function displaySessionId(): void {
    if (typeof window === 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('displaySessionId() can only be called in a browser environment');
        return;
    }

    // Listen for New Relic beacon requests
    const observer = new PerformanceObserver(handleBeaconEntries);

    observer.observe({ entryTypes: ['resource'] });

    // Also provide immediate guidance
    // eslint-disable-next-line no-console
    console.group('%c🔍 New Relic Session ID Helper', 'color: #00AC69; font-weight: bold; font-size: 16px');
    // eslint-disable-next-line no-console
    console.info('Your New Relic session ID can be found using this query:');
    // eslint-disable-next-line no-console
    console.info(
        '%cSELECT count(*) as events, latest(pageUrl) as lastPage\nFROM PageView, AjaxRequest\nWHERE appId = 1120278090\nSINCE 30 minutes ago\nFACET session\nLIMIT 10',
        'background: #f0f0f0; padding: 10px; border-left: 4px solid #00AC69; font-family: monospace; white-space: pre'
    );
    // eslint-disable-next-line no-console
    console.info('📍 Run in: New Relic account 2750272 → Query Your Data');
    // eslint-disable-next-line no-console
    console.info('📋 Your session ID: Look in the "session" column (e.g., ee4f5d2c5cac33d5)');
    // eslint-disable-next-line no-console
    console.info('📊 Use in: Session Journey Dashboard variable');
    // eslint-disable-next-line no-console
    console.groupEnd();

    // Set timeout to disconnect observer if no beacons detected
    setTimeout(() => {
        observer.disconnect();
    }, 5000);
}

/**
 * Adds displaySessionId to window object for easy console access.
 * Call this during app initialization to make the helper available globally.
 *
 * @example
 * // In your app initialization:
 * enableSessionIdHelper();
 *
 * // Then in browser console:
 * window.displaySessionId()
 */
export function enableSessionIdHelper(): void {
    if (typeof window !== 'undefined') {
        (window as any).displaySessionId = displaySessionId;
        // eslint-disable-next-line no-console
        console.info('💡 Type "displaySessionId()" in console to see how to find your New Relic session ID');
    }
}
